import { useEffect, useRef, useState, useCallback } from "react";

interface UseBackgroundMusicOptions {
  trackPath?: string;
  volume?: number;
  loop?: boolean;
  fadeDuration?: number;
  enabled?: boolean;
}

/**
 * Hook to manage background music playback with crossfading between tracks.
 *
 * Uses a dual audio-element pool to handle iOS Safari's autoplay restrictions.
 * iOS blocks play() on newly created Audio elements without a direct user gesture.
 * By pre-creating two elements and "blessing" both during the first unmute
 * (a user-gesture handler), subsequent track changes can crossfade between the
 * two pool elements without requiring additional gestures.
 *
 * Volume is controlled via HTMLAudioElement.volume directly. On iOS Safari this
 * property is read-only (always 1.0), so iOS users hear music at device volume.
 * This is an acceptable tradeoff — routing through Web Audio API's
 * createMediaElementSource() causes clock-drift tempo changes on mobile browsers.
 * See: https://bugs.webkit.org/show_bug.cgi?id=221334
 */
export function useBackgroundMusic({
  trackPath,
  volume = 0.3,
  loop = true,
  fadeDuration = 2000,
  enabled = true,
}: UseBackgroundMusicOptions) {
  // Dual audio element pool — avoids creating new Audio() on track change.
  const poolRef = useRef<[HTMLAudioElement, HTMLAudioElement] | null>(null);
  const activeIdxRef = useRef<0 | 1>(0);
  const fadeTimersRef = useRef<[number | null, number | null]>([null, null]);
  const previousTrackRef = useRef<string | undefined>(undefined);
  const [isPlaying, setIsPlaying] = useState(false);
  const hasUserInteractedRef = useRef(false);
  const isMutedRef = useRef(true);
  const blessedRef = useRef(false);
  const targetVolumeRef = useRef(volume);
  const loopRef = useRef(loop);

  useEffect(() => {
    targetVolumeRef.current = volume;
    // If already unmuted, update the active element's volume directly
    if (!isMutedRef.current && poolRef.current) {
      poolRef.current[activeIdxRef.current].volume = volume;
    }
  }, [volume]);

  // Keep loop property in sync with config
  useEffect(() => {
    loopRef.current = loop;
    const pool = poolRef.current;
    if (!pool) return;
    for (const el of pool) {
      el.loop = loop;
    }
  }, [loop]);

  // Create audio pool once on mount and track user interaction
  useEffect(() => {
    const a = new Audio();
    const b = new Audio();
    for (const el of [a, b]) {
      el.volume = targetVolumeRef.current;
      el.muted = true;
      el.preload = "auto";
      el.loop = loopRef.current;
    }
    poolRef.current = [a, b];

    // Fallback loop handler — some browsers don't reliably honor the native
    // loop property. This listener manually restarts the track when it ends.
    const handleEnded = (e: Event) => {
      const el = e.currentTarget as HTMLAudioElement;
      if (!loopRef.current) return;
      const pool = poolRef.current;
      if (!pool) return;
      // Only restart if this element is the currently active one
      if (pool[activeIdxRef.current] !== el) return;
      el.currentTime = 0;
      el.play().catch(console.warn);
    };
    a.addEventListener("ended", handleEnded);
    b.addEventListener("ended", handleEnded);
    const timers = fadeTimersRef.current;

    const markInteraction = () => {
      hasUserInteractedRef.current = true;
    };
    document.addEventListener("click", markInteraction, { once: true });
    document.addEventListener("touchstart", markInteraction, { once: true });
    document.addEventListener("keydown", markInteraction, { once: true });
    document.addEventListener("scroll", markInteraction, {
      once: true,
      passive: true,
    });
    document.addEventListener("wheel", markInteraction, {
      once: true,
      passive: true,
    });

    return () => {
      a.removeEventListener("ended", handleEnded);
      b.removeEventListener("ended", handleEnded);
      for (const el of [a, b]) {
        el.pause();
        el.removeAttribute("src");
        el.load();
      }
      for (let i = 0; i < 2; i++) {
        if (timers[i]) {
          clearInterval(timers[i]!);
          timers[i] = null;
        }
      }
      poolRef.current = null;
      previousTrackRef.current = undefined;
      activeIdxRef.current = 0;
      blessedRef.current = false;

      document.removeEventListener("click", markInteraction);
      document.removeEventListener("touchstart", markInteraction);
      document.removeEventListener("keydown", markInteraction);
      document.removeEventListener("scroll", markInteraction);
      document.removeEventListener("wheel", markInteraction);
    };
  }, []);

  // Per-element fade helper — fades via element.volume
  // Note: on iOS Safari element.volume is read-only (always 1.0), so fades
  // are inaudible there. Mute/unmute still works via element.muted.
  const fadeElement = useCallback(
    (
      poolIndex: 0 | 1,
      targetVol: number,
      duration: number,
      onComplete?: () => void,
    ) => {
      const pool = poolRef.current;
      if (!pool) {
        onComplete?.();
        return;
      }
      const audio = pool[poolIndex];

      if (fadeTimersRef.current[poolIndex]) {
        clearInterval(fadeTimersRef.current[poolIndex]!);
        fadeTimersRef.current[poolIndex] = null;
      }

      const startVol = audio.volume;
      const delta = targetVol - startVol;
      if (Math.abs(delta) < 0.001) {
        audio.volume = targetVol;
        onComplete?.();
        return;
      }

      const steps = 50;
      const stepDur = duration / steps;
      const stepSize = delta / steps;
      let step = 0;

      const id = window.setInterval(() => {
        step++;
        if (step >= steps) {
          audio.volume = targetVol;
          clearInterval(id);
          fadeTimersRef.current[poolIndex] = null;
          onComplete?.();
        } else {
          audio.volume = Math.max(0, Math.min(1, startVol + stepSize * step));
        }
      }, stepDur);

      fadeTimersRef.current[poolIndex] = id;
    },
    [],
  );

  // Handle track changes by crossfading between pool elements
  useEffect(() => {
    const pool = poolRef.current;
    if (!pool || !enabled) return;

    if (!trackPath) {
      const active = pool[activeIdxRef.current];
      if (!active.paused) active.pause();
      previousTrackRef.current = undefined;
      setIsPlaying(false);
      return;
    }

    if (trackPath === previousTrackRef.current) return;

    const isFirstTrack = previousTrackRef.current === undefined;
    previousTrackRef.current = trackPath;

    if (isFirstTrack) {
      const idx = activeIdxRef.current;
      const audio = pool[idx];
      audio.muted = true;
      audio.src = trackPath;
      audio.load();

      const onReady = () => {
        audio
          .play()
          .then(() => setIsPlaying(true))
          .catch(console.warn);
      };
      audio.addEventListener("canplaythrough", onReady, { once: true });

      return () => {
        audio.removeEventListener("canplaythrough", onReady);
      };
    }

    // Subsequent tracks — crossfade to the other pool element
    const oldIdx = activeIdxRef.current;
    const newIdx: 0 | 1 = oldIdx === 0 ? 1 : 0;
    activeIdxRef.current = newIdx;

    const oldAudio = pool[oldIdx];
    const newAudio = pool[newIdx];

    if (fadeTimersRef.current[newIdx]) {
      clearInterval(fadeTimersRef.current[newIdx]!);
      fadeTimersRef.current[newIdx] = null;
    }

    if (!newAudio.paused) newAudio.pause();

    // Start silent — the fade will bring volume up
    newAudio.volume = 0;

    if (blessedRef.current) {
      newAudio.muted = false;
    } else {
      newAudio.muted = true;
    }
    newAudio.src = trackPath;
    newAudio.currentTime = 0;
    newAudio.load();

    const onReady = () => {
      newAudio
        .play()
        .then(() => {
          setIsPlaying(true);
          const target = isMutedRef.current ? 0 : targetVolumeRef.current;
          fadeElement(newIdx, target, fadeDuration);
        })
        .catch(console.warn);
    };
    newAudio.addEventListener("canplaythrough", onReady, { once: true });

    // Fade out the old track
    if (!oldAudio.paused) {
      fadeElement(oldIdx, 0, fadeDuration / 2, () => {
        oldAudio.pause();
      });
    }

    return () => {
      newAudio.removeEventListener("canplaythrough", onReady);
    };
  }, [trackPath, enabled, fadeDuration, fadeElement]);

  // --- Public API (called from user-gesture handlers) ---

  const unmute = useCallback((targetVolume: number = 0.3) => {
    const pool = poolRef.current;
    if (!pool) return;

    // Cancel any ongoing fades
    for (let i = 0; i < 2; i++) {
      if (fadeTimersRef.current[i]) {
        clearInterval(fadeTimersRef.current[i]!);
        fadeTimersRef.current[i] = null;
      }
    }

    isMutedRef.current = false;
    const activeIdx = activeIdxRef.current;
    const active = pool[activeIdx];

    if (!blessedRef.current) {
      active.currentTime = 0;
    }

    // Unmute and set volume
    active.muted = false;
    active.volume = targetVolume;
    if (active.paused) {
      active.play().catch(console.warn);
    }

    // Bless the inactive element — only if we have a known track path.
    const currentTrack = previousTrackRef.current;
    if (!blessedRef.current && currentTrack) {
      const inactiveIdx: 0 | 1 = activeIdx === 0 ? 1 : 0;
      const inactive = pool[inactiveIdx];
      inactive.muted = false;
      inactive.volume = 0;
      inactive.src = currentTrack;
      inactive.load();
      inactive
        .play()
        .then(() => {
          inactive.pause();
        })
        .catch(console.warn);
      blessedRef.current = true;
    }
  }, []);

  const mute = useCallback(() => {
    const pool = poolRef.current;
    if (!pool) return;

    for (let i = 0; i < 2; i++) {
      if (fadeTimersRef.current[i]) {
        clearInterval(fadeTimersRef.current[i]!);
        fadeTimersRef.current[i] = null;
      }
    }

    isMutedRef.current = true;
    const activeIdx = activeIdxRef.current;
    pool[activeIdx].muted = true;
  }, []);

  const play = useCallback(() => {
    const active = poolRef.current?.[activeIdxRef.current];
    if (active) active.play().catch(console.warn);
  }, []);

  const pause = useCallback(() => {
    const active = poolRef.current?.[activeIdxRef.current];
    if (active) active.pause();
  }, []);

  const setVolume = useCallback((v: number) => {
    const pool = poolRef.current;
    if (pool) {
      pool[activeIdxRef.current].volume = Math.max(0, Math.min(1, v));
    }
  }, []);

  return {
    isPlaying,
    play,
    pause,
    setVolume,
    unmute,
    mute,
  };
}
